/**
 * 调试日志工具
 * 将日志保存到 sessionStorage，即使页面刷新也能查看
 */

interface LogEntry {
  timestamp: string;
  level: 'log' | 'error' | 'warn' | 'info';
  tag: string;
  message: string;
  data?: any;
}

class DebugLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private storageKey = 'debug_logs';

  constructor() {
    this.loadLogs();
  }

  private loadLogs() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load debug logs:', error);
    }
  }

  private saveLogs() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to save debug logs:', error);
    }
  }

  private addLog(level: LogEntry['level'], tag: string, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      tag,
      message,
      data,
    };

    this.logs.push(entry);

    // 限制日志数量
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    this.saveLogs();

    // 同时输出到控制台
    const emoji = {
      log: '📝',
      error: '❌',
      warn: '⚠️',
      info: 'ℹ️',
    }[level];

    const consoleMethod = level === 'error' ? console.error : 
                         level === 'warn' ? console.warn : 
                         console.log;

    consoleMethod(`${emoji} [${tag}] ${message}`, data || '');
  }

  log(tag: string, message: string, data?: any) {
    this.addLog('log', tag, message, data);
  }

  error(tag: string, message: string, data?: any) {
    this.addLog('error', tag, message, data);
  }

  warn(tag: string, message: string, data?: any) {
    this.addLog('warn', tag, message, data);
  }

  info(tag: string, message: string, data?: any) {
    this.addLog('info', tag, message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    sessionStorage.removeItem(this.storageKey);
    console.log('🗑️ Debug logs cleared');
  }

  printLogs() {
    console.group('📋 Debug Logs History');
    this.logs.forEach((log, index) => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      const emoji = {
        log: '📝',
        error: '❌',
        warn: '⚠️',
        info: 'ℹ️',
      }[log.level];
      
      console.log(`${index + 1}. [${time}] ${emoji} [${log.tag}] ${log.message}`);
      if (log.data) {
        console.log('   Data:', log.data);
      }
    });
    console.groupEnd();
  }

  // 导出日志为文本
  exportLogs(): string {
    return this.logs.map((log, index) => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      let text = `${index + 1}. [${time}] [${log.level.toUpperCase()}] [${log.tag}] ${log.message}`;
      if (log.data) {
        text += `\n   Data: ${JSON.stringify(log.data, null, 2)}`;
      }
      return text;
    }).join('\n\n');
  }
}

export const debugLogger = new DebugLogger();

// 在控制台中暴露全局方法，方便调试
if (typeof window !== 'undefined') {
  (window as any).debugLogger = {
    print: () => debugLogger.printLogs(),
    clear: () => debugLogger.clearLogs(),
    export: () => {
      const logs = debugLogger.exportLogs();
      console.log(logs);
      return logs;
    },
    download: () => {
      const logs = debugLogger.exportLogs();
      const blob = new Blob([logs], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-logs-${new Date().toISOString()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
  
  console.log('🔧 Debug Logger 已加载！');
  console.log('💡 使用以下命令：');
  console.log('   debugLogger.print()    - 打印所有日志');
  console.log('   debugLogger.clear()    - 清空日志');
  console.log('   debugLogger.export()   - 导出日志文本');
  console.log('   debugLogger.download() - 下载日志文件');
}
